import { motion } from "framer-motion";
import { Film, Video, Clapperboard, Sparkles, Camera, PlayCircle } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

const StudioServices = () => {
  const services = [
    {
      icon: <Film className="w-10 h-10" />,
      title: "Brand Films",
      description: "Cinematic narratives that capture your brand's essence and connect emotionally with your audience.",
      details: ["Documentary-style storytelling", "Brand origin stories", "Customer testimonials", "Culture films"]
    },
    {
      icon: <Video className="w-10 h-10" />,
      title: "Digital Commercials",
      description: "High-impact ads optimized for social media, streaming platforms, and digital channels.",
      details: ["30s-60s commercials", "Social media ads", "YouTube pre-rolls", "OTT platform content"]
    },
    {
      icon: <Clapperboard className="w-10 h-10" />,
      title: "Social Content",
      description: "Scroll-stopping short-form content designed for Instagram, TikTok, and other social platforms.",
      details: ["Reels & TikToks", "Behind-the-scenes", "Product showcases", "Influencer collaborations"]
    },
    {
      icon: <Sparkles className="w-10 h-10" />,
      title: "Motion Graphics",
      description: "Eye-catching animations and visual effects that elevate your message.",
      details: ["2D/3D animation", "Logo animations", "Explainer videos", "Kinetic typography"]
    },
    {
      icon: <Camera className="w-10 h-10" />,
      title: "Product & Lifestyle",
      description: "Professional photography and videography that showcases your products in their best light.",
      details: ["Product shoots", "Lifestyle photography", "E-commerce content", "Lookbook production"]
    },
    {
      icon: <PlayCircle className="w-10 h-10" />,
      title: "Creative Direction",
      description: "End-to-end creative strategy and art direction for campaigns that demand attention.",
      details: ["Concept development", "Art direction", "Campaign strategy", "Brand positioning"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] studio-body">
      <SEO brand="studio" path="/studio/services" title="Services" description="Brand films, commercials, motion graphics, and cinematic social content by Wake N' Bake Studio." />
      <Navigation brand="studio" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 film-grain">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="studio-services-heading">
              VISUALS THAT
              <br />
              <span className="text-[#F97316]">MOVE BRANDS</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From concept to final cut, we create cinematic experiences that captivate audiences and drive results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0A] border-2 border-[#262626] hover:border-[#F97316]/50 rounded-none p-8 transition-all group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#F97316]/10 flex items-center justify-center mb-6 text-[#F97316] group-hover:bg-[#F97316]/20 transition-all">
                  {service.icon}
                </div>
                <h3 className="heading-font text-2xl text-white mb-4">{service.title}</h3>
                <p className="text-gray-300 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <div className="w-1 h-1 bg-[#F97316] mt-2 flex-shrink-0"></div>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Showreel Teaser */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
              SEE OUR WORK IN ACTION
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Explore our portfolio of brand films, commercials, and creative content.
            </p>
            <Link
              to="/studio/portfolio"
              className="inline-block px-8 py-4 bg-[#F97316] text-white rounded-none font-medium hover:bg-[#EA580C] transition-colors"
              data-testid="view-portfolio-btn"
            >
              View Portfolio
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
              INDUSTRIES WE SERVE
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From startups to Fortune 500s, we've crafted visual stories across every sector.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {["Fashion & Lifestyle", "Technology", "Food & Beverage", "Automotive", "Real Estate", "Healthcare", "Sports & Fitness", "Entertainment"].map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#0A0A0A] border border-[#262626] p-6 text-center hover:border-[#F97316]/50 transition-all"
              >
                <div className="text-gray-300 font-medium">{industry}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="border-2 border-[#3B82F6]/30 p-12 rounded-none"
          >
            <h2 className="heading-font text-4xl text-white mb-6">
              WANT AI-DRIVEN GROWTH?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Explore Brandify Official for marketing, tech, and AI solutions.
            </p>
            <Link
              to="/brandify/services"
              className="inline-block px-8 py-4 bg-[#3B82F6] text-white rounded-full font-medium hover:bg-[#2563EB] transition-colors"
              data-testid="visit-brandify-cta"
            >
              Visit Brandify →
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer brand="studio" />
    </div>
  );
};

export default StudioServices;